import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowRight, 
  TrendingUp, 
  Shield, 
  Globe, 
  Zap,
  Building2,
  CreditCard,
  Fingerprint,
  Brain,
  Umbrella,
  ChevronRight,
  Users,
  Target,
  BarChart3,
  Handshake,
  Mail,
  Send,
  Loader2,
  CheckCircle,
  Landmark
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_africa-ventures/artifacts/mjqy16nm_image.png";
const HEADER_LOGO_URL = "https://customer-assets.emergentagent.com/job_africa-ventures/artifacts/mjqy16nm_image.png";

// Portfolio companies data
const portfolioCompanies = [
  {
    name: "OnePay / Tamba",
    layer: "Payments Layer",
    description: "Digital wallets, merchant payments, cross-border infrastructure",
    icon: CreditCard,
    color: "bg-blue-500",
    urls: [
      { label: "Tamba", url: "https://tamba.app/" },
      { label: "OnePay", url: "https://onepay.cash/" }
    ]
  },
  {
    name: "OnePass / OneIdentity",
    layer: "Identity Layer", 
    description: "KYC and onboarding, authentication, access management",
    icon: Fingerprint,
    color: "bg-emerald-500",
    urls: [
      { label: "OnePass", url: "https://infinityonepass.com/" },
      { label: "OneIdentity", url: "https://onepassinfinity.com/" }
    ]
  },
  {
    name: "Uwatu",
    layer: "Credit Layer",
    description: "AI-driven lending, financial inclusion",
    icon: TrendingUp,
    color: "bg-amber-500",
    url: "https://myuwatu.com/landing"
  },
  {
    name: "Valora",
    layer: "Real Assets Layer",
    description: "Fractional investment in real estate, infrastructure, and commodities",
    icon: Landmark,
    color: "bg-cyan-500",
    url: "https://valora.inc/"
  },
  {
    name: "Reverence",
    layer: "Financial Services",
    description: "Insurance, wealth management, financial products",
    icon: Umbrella,
    color: "bg-purple-500",
    url: "https://reverence.africa/"
  },
  {
    name: "Apaka",
    layer: "Intelligence Layer",
    description: "AI and data infrastructure, risk decisioning, fraud detection",
    icon: Brain,
    color: "bg-rose-500",
    url: "https://apaka.ai/"
  }
];

// Strategic partners
const partners = [
  { name: "MTN", description: "African Distribution" },
  { name: "Rapyd", description: "Payments Infrastructure" },
  { name: "Pagaya", description: "AI Credit Underwriting" },
  { name: "AU10TIX", description: "Identity Verification" }
];

// Investment stats
const stats = [
  { label: "Ticket Size", value: "$250K - $2M" },
  { label: "Ownership Target", value: "20% - 40%" },
  { label: "Investment Stage", value: "Pre-seed to Growth" },
  { label: "Focus", value: "Pan-African Fintech" }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [hoveredCompany, setHoveredCompany] = useState(null);
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    company: '',
    investment_interest: '',
    message: ''
  });
  const [submittingInquiry, setSubmittingInquiry] = useState(false);
  const [inquirySubmitted, setInquirySubmitted] = useState(false);

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (!inquiryForm.name || !inquiryForm.email || !inquiryForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmittingInquiry(true);
    try {
      await axios.post(`${API_URL}/api/inquiries`, inquiryForm);
      setInquirySubmitted(true);
      setInquiryForm({ name: '', email: '', company: '', investment_interest: '', message: '' });
      toast.success('Inquiry submitted successfully');
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmittingInquiry(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <img 
              src={HEADER_LOGO_URL} 
              alt="Berkshire Africa" 
              className="h-12 md:h-14 w-auto object-contain"
              data-testid="landing-logo"
            />
            <div className="hidden md:flex items-center gap-8">
              <a href="#about" className="text-sm text-slate-600 hover:text-[#1E3A5F] transition-colors">About</a>
              <a href="#portfolio" className="text-sm text-slate-600 hover:text-[#1E3A5F] transition-colors">Portfolio</a>
              <a href="#strategy" className="text-sm text-slate-600 hover:text-[#1E3A5F] transition-colors">Strategy</a>
              <a href="#partners" className="text-sm text-slate-600 hover:text-[#1E3A5F] transition-colors">Partners</a>
              <a href="#contact" className="text-sm text-slate-600 hover:text-[#1E3A5F] transition-colors">Contact</a>
            </div>
            <Button 
              onClick={() => navigate('/login')}
              className="bg-[#1E3A5F] hover:bg-[#152c4a] text-white"
              data-testid="login-nav-btn"
            >
              Investor Portal
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <Badge className="bg-[#1E3A5F]/10 text-[#1E3A5F] hover:bg-[#1E3A5F]/20 mb-6">
              Pan-African Investment Platform
            </Badge>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-6">
              Building Africa's
              <span className="block text-[#1E3A5F]">Financial Infrastructure</span>
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mb-8 leading-relaxed">
              Berkshire Africa is a Pan-African investment holding company building a fully integrated financial operating system—spanning payments, identity, credit, and AI-driven intelligence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={() => navigate('/login')}
                className="bg-[#1E3A5F] hover:bg-[#152c4a] text-white text-lg px-8"
                data-testid="hero-cta-btn"
              >
                Access Investor Portal
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F]/5 text-lg px-8"
                onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-[#1E3A5F]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-2xl md:text-3xl font-heading font-bold text-white mb-1">{stat.value}</p>
                <p className="text-sm text-slate-300 uppercase tracking-widest">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-emerald-100 text-emerald-700 mb-4">Our Vision</Badge>
              <h2 className="font-heading text-4xl font-bold text-slate-900 mb-6">
                A Berkshire Hathaway for Africa's Digital Economy
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                We operate a <strong>permanent capital model</strong>, combining founder-led venture investing with long-term strategic holdings to create enduring, scalable businesses across Africa.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Unlike traditional VCs, we build <strong>interconnected platforms</strong> designed to function as a unified financial operating system—not isolated startups.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-[#1E3A5F]/10 rounded-lg">
                    <Target className="h-5 w-5 text-[#1E3A5F]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Founder-Funded</h4>
                    <p className="text-slate-600 text-sm">High-conviction, concentrated portfolio with active operational involvement</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-[#1E3A5F]/10 rounded-lg">
                    <Globe className="h-5 w-5 text-[#1E3A5F]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Pan-African Scale</h4>
                    <p className="text-slate-600 text-sm">Building across Africa's largest and fastest-growing markets</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-[#1E3A5F]/10 rounded-lg">
                    <Shield className="h-5 w-5 text-[#1E3A5F]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Long-Term Holdings</h4>
                    <p className="text-slate-600 text-sm">No forced exit timelines—strategic compounding over decades</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-2xl p-8">
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-6">Business Model</h3>
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <div className="flex items-center gap-3 mb-3">
                    <Zap className="h-5 w-5 text-amber-500" />
                    <h4 className="font-semibold text-slate-900">Venture Capital (Early Stage)</h4>
                  </div>
                  <p className="text-slate-600 text-sm">Pre-seed and seed investments, fully funded by founder capital, focused on building a coordinated ecosystem</p>
                </div>
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <div className="flex items-center gap-3 mb-3">
                    <BarChart3 className="h-5 w-5 text-blue-500" />
                    <h4 className="font-semibold text-slate-900">Growth & Structured Capital</h4>
                  </div>
                  <p className="text-slate-600 text-sm">Follow-on investments with flexible capital structures—equity, debt, and hybrid instruments</p>
                </div>
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <div className="flex items-center gap-3 mb-3">
                    <Building2 className="h-5 w-5 text-emerald-500" />
                    <h4 className="font-semibold text-slate-900">Long-Term Holdings</h4>
                  </div>
                  <p className="text-slate-600 text-sm">Minority ownership positions with permanent capital approach and no forced exit pressure</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-24 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-[#1E3A5F]/10 text-[#1E3A5F] mb-4">Africa Fintech Operating System</Badge>
            <h2 className="font-heading text-4xl font-bold text-slate-900 mb-6">
              Multi-Layered Financial Infrastructure Stack
            </h2>
            <p className="text-lg text-slate-600">
              We're building interconnected platforms that create strong network effects, lower customer acquisition costs, and high defensibility across the financial value chain.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolioCompanies.map((company, index) => {
              const hasLink = company.url || company.urls;
              
              return (
                <Card 
                  key={index}
                  className={`transition-all duration-300 h-full ${
                    hoveredCompany === index ? 'shadow-lg -translate-y-1 border-[#1E3A5F]/30' : 'border-slate-200'
                  }`}
                  onMouseEnter={() => setHoveredCompany(index)}
                  onMouseLeave={() => setHoveredCompany(null)}
                  data-testid={`portfolio-card-${index}`}
                >
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 ${company.color} rounded-xl flex items-center justify-center mb-4`}>
                      <company.icon className="h-6 w-6 text-white" />
                    </div>
                    <Badge variant="secondary" className="mb-3 text-xs">{company.layer}</Badge>
                    <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                      {company.name}
                    </h3>
                    <p className="text-slate-600 text-sm mb-3">{company.description}</p>
                    
                    {/* Links */}
                    {company.url && (
                      <a 
                        href={company.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-[#1E3A5F] hover:underline font-medium"
                      >
                        Visit Website <ArrowRight className="h-3 w-3 ml-1" />
                      </a>
                    )}
                    {company.urls && (
                      <div className="flex flex-wrap gap-3">
                        {company.urls.map((link, i) => (
                          <a 
                            key={i}
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-sm text-[#1E3A5F] hover:underline font-medium"
                          >
                            {link.label} <ArrowRight className="h-3 w-3 ml-1" />
                          </a>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Ecosystem Flywheel */}
          <div className="mt-16 bg-white rounded-2xl p-8 border border-slate-200">
            <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-6 text-center">Ecosystem Flywheel</h3>
            <div className="flex flex-wrap justify-center gap-4 items-center">
              {[
                "Payments generate transaction data",
                "Identity enables onboarding", 
                "AI processes behavioral insights",
                "Credit expands access to capital",
                "Financial services increase LTV"
              ].map((step, index) => (
                <div key={index} className="flex items-center">
                  <div className="bg-[#1E3A5F]/5 rounded-lg px-4 py-2">
                    <span className="text-sm text-slate-700">{step}</span>
                  </div>
                  {index < 4 && <ChevronRight className="h-5 w-5 text-slate-400 mx-2" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Strategy Section */}
      <section id="strategy" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-amber-100 text-amber-700 mb-4">Competitive Advantage</Badge>
            <h2 className="font-heading text-4xl font-bold text-slate-900 mb-6">
              Why Berkshire Africa Wins
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-[#1E3A5F] to-[#2d4a6f] rounded-2xl p-8 text-white">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-6">
                <Globe className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-semibold mb-4">Integrated Platform Model</h3>
              <p className="text-slate-200 leading-relaxed">
                Unlike single-product fintechs, we build across multiple layers of the financial stack—creating a unified operating system that compounds value.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-200">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-6">
                <Handshake className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-4">Distribution + Tech Arbitrage</h3>
              <p className="text-slate-600 leading-relaxed">
                African market access via telecom partnerships combined with global fintech innovation leveraging Global Strategic Partners—giving the best of both worlds.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-200">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-4">Permanent Capital Structure</h3>
              <p className="text-slate-600 leading-relaxed">
                Long-term ownership with no forced exit pressure. We compound strategically over decades, not quarters.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-200">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <Brain className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-4">Data-Driven Network Effects</h3>
              <p className="text-slate-600 leading-relaxed">
                Proprietary data across payments, identity, and credit enables continuous improvement of decision-making systems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section id="partners" className="py-24 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-purple-100 text-purple-700 mb-4">Strategic Partnerships</Badge>
            <h2 className="font-heading text-4xl font-bold text-slate-900 mb-6">
              Global Technology Meets African Distribution
            </h2>
            <p className="text-lg text-slate-600">
              We integrate best-in-class infrastructure from global leaders while leveraging Africa's largest distribution networks.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {partners.map((partner, index) => (
              <Card key={index} className="text-center" data-testid={`partner-card-${index}`}>
                <CardContent className="p-6">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="font-heading font-bold text-xl text-[#1E3A5F]">{partner.name[0]}</span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1">{partner.name}</h3>
                  <p className="text-sm text-slate-500">{partner.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-[#1E3A5F]/10 text-[#1E3A5F] mb-4">Leadership</Badge>
              <h2 className="font-heading text-4xl font-bold text-slate-900 mb-6">
                Joseph Poche
              </h2>
              <p className="text-xl text-slate-600 mb-2">Founder & Principal Investor</p>
              <div className="w-16 h-1 bg-[#1E3A5F] mb-6"></div>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Serial entrepreneur and investor with a track record of building multiple African technology platforms. Joseph leads Berkshire Africa Ventures as the primary capital provider, focused on long-term value creation across the continent.
              </p>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-center gap-2">
                  <ChevronRight className="h-4 w-4 text-[#1E3A5F]" />
                  Builder of multiple African technology platforms
                </li>
                <li className="flex items-center gap-2">
                  <ChevronRight className="h-4 w-4 text-[#1E3A5F]" />
                  Lead capital provider for Berkshire Africa Ventures
                </li>
                <li className="flex items-center gap-2">
                  <ChevronRight className="h-4 w-4 text-[#1E3A5F]" />
                  Focused on long-term, compounding value creation
                </li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-[#1E3A5F] to-[#2d4a6f] rounded-2xl p-12">
              <div className="text-center">
                <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="h-16 w-16 text-white" />
                </div>
                <blockquote className="text-xl text-white font-light italic mb-6">
                  "We're building a Berkshire Hathaway for Africa's digital economy—investing early, scaling strategically, holding long-term."
                </blockquote>
                <p className="text-slate-300">— Joseph Poche</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-[#1E3A5F]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Access the Portfolio?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Limited Partners and team members can access real-time portfolio data, analytics, and deal flow through our secure investor portal.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/login')}
            className="bg-white text-[#1E3A5F] hover:bg-slate-100 text-lg px-10"
            data-testid="cta-btn"
          >
            Access Investor Portal
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* LP Inquiry Form Section */}
      <section id="contact" className="py-24 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <Badge className="bg-[#1E3A5F]/10 text-[#1E3A5F] mb-4">Get In Touch</Badge>
              <h2 className="font-heading text-4xl font-bold text-slate-900 mb-6">
                Interested in Partnering with Us?
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Whether you're a potential Limited Partner, strategic partner, or founder with a compelling Africa-focused fintech opportunity, we'd love to hear from you.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#1E3A5F]/10 rounded-lg">
                    <Mail className="h-5 w-5 text-[#1E3A5F]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">For Investors</h4>
                    <p className="text-slate-600 text-sm">Learn about LP opportunities and our fund structure</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#1E3A5F]/10 rounded-lg">
                    <Handshake className="h-5 w-5 text-[#1E3A5F]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">For Partners</h4>
                    <p className="text-slate-600 text-sm">Explore strategic collaboration opportunities</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#1E3A5F]/10 rounded-lg">
                    <Target className="h-5 w-5 text-[#1E3A5F]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">For Founders</h4>
                    <p className="text-slate-600 text-sm">Share your Africa fintech venture for consideration</p>
                  </div>
                </div>
              </div>
            </div>
            
            <Card className="border-slate-200" data-testid="inquiry-form-card">
              <CardContent className="p-8">
                {inquirySubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="h-8 w-8 text-emerald-600" />
                    </div>
                    <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-3">Thank You!</h3>
                    <p className="text-slate-600">
                      We've received your inquiry and will be in touch shortly.
                    </p>
                    <Button 
                      variant="outline" 
                      className="mt-6"
                      onClick={() => setInquirySubmitted(false)}
                    >
                      Submit Another Inquiry
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleInquirySubmit} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="inquiry-name">Full Name *</Label>
                        <Input
                          id="inquiry-name"
                          value={inquiryForm.name}
                          onChange={(e) => setInquiryForm({...inquiryForm, name: e.target.value})}
                          placeholder="John Doe"
                          required
                          data-testid="inquiry-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="inquiry-email">Email *</Label>
                        <Input
                          id="inquiry-email"
                          type="email"
                          value={inquiryForm.email}
                          onChange={(e) => setInquiryForm({...inquiryForm, email: e.target.value})}
                          placeholder="john@company.com"
                          required
                          data-testid="inquiry-email"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="inquiry-company">Company / Organization</Label>
                      <Input
                        id="inquiry-company"
                        value={inquiryForm.company}
                        onChange={(e) => setInquiryForm({...inquiryForm, company: e.target.value})}
                        placeholder="Acme Capital"
                        data-testid="inquiry-company"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="inquiry-interest">Area of Interest</Label>
                      <Select 
                        value={inquiryForm.investment_interest} 
                        onValueChange={(value) => setInquiryForm({...inquiryForm, investment_interest: value})}
                      >
                        <SelectTrigger data-testid="inquiry-interest">
                          <SelectValue placeholder="Select your interest" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lp_investment">LP Investment Opportunity</SelectItem>
                          <SelectItem value="strategic_partnership">Strategic Partnership</SelectItem>
                          <SelectItem value="founder_pitch">Founder Pitch / Deal Submission</SelectItem>
                          <SelectItem value="media_press">Media / Press Inquiry</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="inquiry-message">Message *</Label>
                      <Textarea
                        id="inquiry-message"
                        value={inquiryForm.message}
                        onChange={(e) => setInquiryForm({...inquiryForm, message: e.target.value})}
                        placeholder="Tell us about yourself and how we can help..."
                        rows={4}
                        required
                        data-testid="inquiry-message"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-[#1E3A5F] hover:bg-[#152c4a]"
                      disabled={submittingInquiry}
                      data-testid="submit-inquiry-btn"
                    >
                      {submittingInquiry ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Submit Inquiry
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-8">
            {/* Top row */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-3">
                <div className="bg-white rounded-lg p-2">
                  <img 
                    src={HEADER_LOGO_URL} 
                    alt="Berkshire Africa" 
                    className="h-10 w-auto object-contain"
                  />
                </div>
              </div>
              
              {/* Social Media Links */}
              <div className="flex items-center gap-4">
                <a 
                  href="https://linkedin.com/company/berkshireafrica" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
                  aria-label="LinkedIn"
                >
                  <svg className="h-5 w-5 text-slate-400 hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a 
                  href="https://twitter.com/berkshireafrica" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
                  aria-label="X (Twitter)"
                >
                  <svg className="h-5 w-5 text-slate-400 hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a 
                  href="mailto:invest@berkshireafrica.com"
                  className="p-2 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
                  aria-label="Email"
                >
                  <svg className="h-5 w-5 text-slate-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </a>
              </div>

              <div className="flex gap-6">
                <Link to="/privacy" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy</Link>
                <Link to="/terms" className="text-slate-400 hover:text-white text-sm transition-colors">Terms</Link>
                <Link to="/contact" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</Link>
              </div>
            </div>
            
            {/* Bottom row */}
            <div className="border-t border-slate-800 pt-6 text-center">
              <p className="text-slate-400 text-sm">
                © 2025 Berkshire Africa Inc. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
